import { ApiService } from "./api.service";
import type {
  TProjectWorkflow,
  TProjectWorkflowTransition,
  TProjectWorkflowTransitionApproval,
  TProjectWorkflowTransitionActivity,
} from "@/types/project-workflow";

export interface TUpdateWorkflowStateData {
  allow_project_creation: boolean;
}

export interface TCreateWorkflowTransitionData {
  workflow_id: string;
  transition_state_id: string;
  rejection_state_id?: string | null;
  required_approvals?: number | null;
}

export interface TUpdateWorkflowTransitionData {
  transition_state_id?: string;
  rejection_state_id?: string | null;
  required_approvals?: number | null;
  require_approval?: boolean;
}

export interface TSetWorkflowMoversData {
  mover_ids: string[];
}

export interface TSetWorkflowApproversData {
  approver_ids: string[];
}

export interface TWorkflowApprovalData {
  comment?: string | null;
}

export class ProjectWorkflowService extends ApiService {
  private basePath(slug: string, projectId: string): string {
    return `/api/workspaces/${slug}/projects/${projectId}`;
  }

  async listWorkflowStates(slug: string, projectId: string): Promise<TProjectWorkflow[]> {
    return this.get(`${this.basePath(slug, projectId)}/workflow-states/`);
  }

  async listWorkflowsBatch(
    slug: string,
    projectIds: string[],
  ): Promise<Record<string, TProjectWorkflow[]>> {
    if (projectIds.length === 0) return {};
    const qs = `?project_ids=${encodeURIComponent(projectIds.join(","))}`;
    return this.get(`/api/workspaces/${slug}/project-workflows/${qs}`);
  }

  async updateWorkflowState(
    slug: string,
    projectId: string,
    stateId: string,
    data: TUpdateWorkflowStateData,
  ): Promise<TProjectWorkflow> {
    return this.patch(`${this.basePath(slug, projectId)}/workflow-states/${stateId}/`, data);
  }

  async resetWorkflow(slug: string, projectId: string): Promise<void> {
    return this.delete(`${this.basePath(slug, projectId)}/workflow-reset/`);
  }

  async createTransition(
    slug: string,
    projectId: string,
    data: TCreateWorkflowTransitionData,
  ): Promise<TProjectWorkflowTransition> {
    return this.post(`${this.basePath(slug, projectId)}/workflow-transitions/`, data);
  }

  async updateTransition(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TUpdateWorkflowTransitionData,
  ): Promise<TProjectWorkflowTransition> {
    return this.patch(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/`,
      data,
    );
  }

  async deleteTransition(slug: string, projectId: string, transitionId: string): Promise<void> {
    return this.delete(`${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/`);
  }

  async setMovers(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TSetWorkflowMoversData,
  ): Promise<void> {
    return this.post(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/movers/`,
      data,
    );
  }

  async setApprovers(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TSetWorkflowApproversData,
  ): Promise<TProjectWorkflowTransition> {
    return this.post(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/approvers/`,
      data,
    );
  }

  async submitApproval(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TWorkflowApprovalData,
  ): Promise<TProjectWorkflowTransitionApproval> {
    return this.post(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/approve/`,
      data,
    );
  }

  async submitRejection(
    slug: string,
    projectId: string,
    transitionId: string,
    data: TWorkflowApprovalData,
  ): Promise<TProjectWorkflowTransitionApproval> {
    return this.post(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/reject/`,
      data,
    );
  }

  async requestApproval(slug: string, projectId: string, transitionId: string): Promise<void> {
    return this.post(
      `${this.basePath(slug, projectId)}/workflow-transitions/${transitionId}/request/`,
      {},
    );
  }

  async listActivity(
    slug: string,
    projectId: string,
    params?: Record<string, string>,
  ): Promise<TProjectWorkflowTransitionActivity[]> {
    return this.get(`${this.basePath(slug, projectId)}/workflow-activity/`, { params });
  }
}
